export interface HistoryFacet {
  id: string
  code: string
  facetCode: string
}

export interface ProductHistoryItem {
  id: string
  slug: string
  ts: number
  facets?: HistoryFacet[]
}

export interface ProductHistoryInput {
  id: string | number
  slug: string
  facets?: readonly HistoryFacet[]
}

export interface HistoryRecommendationProfile {
  facets: { facetValueId: string, weight: number }[]
  gender?: string
}

export interface HistoryRecommendationCandidate {
  id: string | number
  slug: string
  modelKey?: string | null
  inStock: boolean
}

export const PRODUCT_HISTORY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
export const HISTORY_RECOMMENDATION_CACHE_MS = 5 * 60 * 1000

const HISTORY_LIMIT = 12
const PROFILE_HISTORY_LIMIT = 10
const PROFILE_FACET_LIMIT = 8
const FACET_WEIGHTS: Record<string, number> = { category: 4, collection: 3, material: 3, color: 1 }

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined
}

function identifier(value: unknown): string | undefined {
  const id = typeof value === 'string' ? value.trim() : typeof value === 'number' && Number.isSafeInteger(value) ? String(value) : ''
  return id.length > 0 && id.length <= 64 ? id : undefined
}

function historyFacets(value: unknown): HistoryFacet[] {
  if (!Array.isArray(value))
    return []

  const facets = new Map<string, HistoryFacet>()
  for (const candidate of value) {
    const item = record(candidate)
    const id = identifier(item?.id)
    const code = typeof item?.code === 'string' ? item.code.trim() : ''
    const facetCode = typeof item?.facetCode === 'string' ? item.facetCode.trim() : ''
    if (id && code && code.length <= 128 && (Object.hasOwn(FACET_WEIGHTS, facetCode) || facetCode === 'gender'))
      facets.set(id, { id, code, facetCode })
  }
  // Keep gender and category even when malformed old storage contains many colors.
  return [...facets.values()]
    .sort((a, b) => (b.facetCode === 'gender' ? 5 : FACET_WEIGHTS[b.facetCode] ?? 0) - (a.facetCode === 'gender' ? 5 : FACET_WEIGHTS[a.facetCode] ?? 0) || a.id.localeCompare(b.id))
    .slice(0, 16)
}

/** Local storage can contain an older full search result, duplicates or invalid data. */
export function normalizeProductHistory(value: unknown, now = Date.now()): ProductHistoryItem[] {
  if (!Array.isArray(value))
    return []

  const entries: ProductHistoryItem[] = []
  for (const candidate of value) {
    const item = record(candidate)
    const id = identifier(item?.id)
    if (!id || typeof item?.slug !== 'string' || !item.slug.trim() || item.slug.length > 256
      || typeof item.ts !== 'number' || !Number.isFinite(item.ts) || item.ts > now || now - item.ts >= PRODUCT_HISTORY_MAX_AGE_MS) {
      continue
    }
    entries.push({ id, slug: item.slug.trim(), ts: item.ts, facets: historyFacets(item.facets) })
  }

  const seen = new Set<string>()
  return entries.sort((a, b) => b.ts - a.ts)
    .filter((item) => {
      if (seen.has(item.id))
        return false
      seen.add(item.id)
      return true
    })
    .slice(0, HISTORY_LIMIT)
}

export function recordProductHistory(history: unknown, item: ProductHistoryInput, now = Date.now()): ProductHistoryItem[] {
  const previous = normalizeProductHistory(history, now)
  const id = identifier(item.id)
  const facets = item.facets ?? previous.find(entry => entry.id === id)?.facets
  // Select fields explicitly: product grid callers pass full search results.
  return normalizeProductHistory([{ id, slug: item.slug, ts: now, facets }, ...previous], now)
}

/** Coarse recency bands keep a profile reusable while still favouring recent interests. */
export function buildHistoryRecommendationProfile(history: unknown, gender?: string, now = Date.now()): HistoryRecommendationProfile {
  const targetGender = gender === 'women' || gender === 'men' ? gender : undefined
  const scores = new Map<string, { facetValueId: string, facetCode: string, score: number }>()
  const recent = normalizeProductHistory(history, now).slice(0, PROFILE_HISTORY_LIMIT)

  recent.forEach((item, index) => {
    const genders = item.facets?.filter(facet => facet.facetCode === 'gender').map(facet => facet.code) ?? []
    if (targetGender && genders.length && !genders.includes(targetGender) && !genders.includes('unisex'))
      return

    const recency = index < 3 ? 3 : index < 6 ? 2 : 1
    for (const facet of item.facets ?? []) {
      const importance = FACET_WEIGHTS[facet.facetCode]
      if (!importance)
        continue
      const previous = scores.get(facet.id)?.score ?? 0
      scores.set(facet.id, { facetValueId: facet.id, facetCode: facet.facetCode, score: previous + importance * recency })
    }
  })

  const ranked = [...scores.values()].sort((a, b) => b.score - a.score || a.facetValueId.localeCompare(b.facetValueId))
  const category = ranked.find(facet => facet.facetCode === 'category')
  const selected = category ? [category] : []
  let colors = 0
  for (const facet of ranked) {
    if (selected.length >= PROFILE_FACET_LIMIT)
      break
    if (facet === category || (facet.facetCode === 'color' && colors >= 2))
      continue
    selected.push(facet)
    if (facet.facetCode === 'color')
      colors++
  }

  const maxScore = ranked[0]?.score ?? 1
  return {
    ...(targetGender ? { gender: targetGender } : {}),
    facets: selected.map(facet => ({
      facetValueId: facet.facetValueId,
      weight: Math.max(1, Math.round(facet.score / maxScore * 10)),
    })).sort((a, b) => a.facetValueId.localeCompare(b.facetValueId)),
  }
}

/** Exclusions stay local so changing the current product can reuse the cached candidate pool. */
export function selectHistoryRecommendationProducts<T extends HistoryRecommendationCandidate>(
  products: readonly T[],
  options: {
    productId: string | number
    productSlug: string
    history: readonly Pick<ProductHistoryItem, 'id' | 'slug'>[]
    otherProductIds: readonly (string | number)[]
  },
): T[] {
  const excludedIds = new Set([String(options.productId), ...options.history.map(item => String(item.id))])
  const excludedSlugs = new Set([options.productSlug, ...options.history.map(item => item.slug)])
  const otherIds = new Set(options.otherProductIds.map(String))
  const candidates = products.filter(item => item.inStock && !excludedIds.has(String(item.id)) && !excludedSlugs.has(item.slug))
  const selected: T[] = []
  const selectedIds = new Set<string>()
  const designs = new Map<string, number>()

  // First prefer different designs and cards absent from the other rows. A
  // second colorway and then overlap can fill a sparse pool; history never can.
  for (const allowOverlap of [false, true]) {
    for (const perDesign of [1, 2]) {
      for (const item of candidates) {
        if (selected.length === 8)
          return selected
        const id = String(item.id)
        const design = item.modelKey || id
        if (selectedIds.has(id) || (!allowOverlap && otherIds.has(id)) || (designs.get(design) ?? 0) >= perDesign)
          continue
        selected.push(item)
        selectedIds.add(id)
        designs.set(design, (designs.get(design) ?? 0) + 1)
      }
    }
  }
  return selected
}
